'use client';

import { downloadDocumentPdf } from '@lib/contentful-pdf/download-document-pdf';
import { logger } from '@sentry/core';
import { DashboardTravelItineraryIntroduction } from '@tuic/web-compositions/my-trips/DashboardTravelItineraryIntroduction';
import { useState } from 'react';

import type { DocumentPdfPayload } from '@lib/contentful-pdf/download-document-pdf';
import type { ComponentBlock } from '@modules/prism-client/types/types';
import type { ButtonProps } from '@tuic/web-compositions/core/Button';
import type { IconName } from '@tuic/web-compositions/core/Icon';
import type { DashboardTravelItineraryIntroductionProps } from '@tuic/web-compositions/my-trips/DashboardTravelItineraryIntroduction';
import type { ReactNode } from 'react';

export type InstructionsPdfButton = DocumentPdfPayload & {
  label: ReactNode;
  variant?: ButtonProps['variant'];
  on?: ButtonProps['on'];
  iconLeft?: IconName;
  iconRight?: IconName;
  dataAttributes?: Record<string, string | undefined>;
};

export type TransitInstructionsBlockData = Omit<
  DashboardTravelItineraryIntroductionProps,
  'button'
> & {
  button?: ButtonProps;
  pdfDownload?: InstructionsPdfButton;
};

export const TransitInstructionsBlock: ComponentBlock<
  TransitInstructionsBlockData
> = ({ data, fallback }) => {
  const [isGenerating, setIsGenerating] = useState(false);

  if (fallback) {
    return <DashboardTravelItineraryIntroduction fallback />;
  }

  if (!data) {
    return null;
  }

  const { pdfDownload, button: staticButton, ...rest } = data;

  const button: ButtonProps | undefined = pdfDownload
    ? {
        ...pdfDownload.dataAttributes,
        variant: pdfDownload.variant,
        on: pdfDownload.on,
        iconLeft: pdfDownload.iconLeft,
        iconRight: pdfDownload.iconRight,
        indicating: isGenerating,
        disabled: isGenerating,
        onClick: async () => {
          setIsGenerating(true);
          try {
            await downloadDocumentPdf({
              pages: pdfDownload.pages,
              fileName: pdfDownload.fileName,
            });
          } catch (error) {
            logger.error(
              `TransitInstructionsBlock: PDF download failed: ${String(error)}`,
            );
          } finally {
            setIsGenerating(false);
          }
        },
        children: pdfDownload.label,
      }
    : staticButton;

  return <DashboardTravelItineraryIntroduction {...rest} button={button} />;
};
